import React from 'react';
import { WidgetProps } from '@rjsf/core';
import * as Icons from 'components/icons';
import { OutlinedTextWidget } from 'components/form-elements/JsonForms/components/Text';
import { Button } from 'components/form-elements';
import { Box } from 'components/layout';
import { Spacer } from 'components/Spacer/Spacer';
import { Text } from 'components/typography';
import { LocaleKey } from 'translations';
import { useLocationAccess } from 'features/location';

/**
 * Widget for the optional "GPS Coordinates" header field.
 *
 * Renders the standard text input, plus - while the field is empty - a small
 * "add coordinates automatically" button. Clicking it requests the device
 * location (triggering the browser permission prompt if needed) and fills the
 * field.
 *
 * This gives the user full control: they can clear the value and re-add it on
 * demand, e.g. if they previously denied the permission (in the app pop-up or
 * the browser prompt) and later changed their mind. The button is hidden once a
 * value is present to keep the header clean.
 */
export function GpsCoordinatesWidget(props: WidgetProps) {
  const { enableLocationAccess, isGeolocationSupported } = useLocationAccess();

  const [isLoading, setIsLoading] = React.useState(false);
  const [errorKey, setErrorKey] = React.useState<LocaleKey | null>(null);

  const hasValue = Boolean(props.value);
  const isEditable = !props.disabled && !props.readonly;
  const showAddButton = !hasValue && isEditable && isGeolocationSupported;

  const handleAddAutomatically = React.useCallback(async () => {
    setIsLoading(true);
    setErrorKey(null);
    try {
      const coordinates = await enableLocationAccess();
      if (coordinates) {
        props.onChange(coordinates);
      } else {
        // Position could not be obtained (unsupported / denied / timed out).
        setErrorKey('relining.gpsCoordinates.error');
      }
    } finally {
      setIsLoading(false);
    }
  }, [enableLocationAccess, props]);

  return (
    <Box>
      <OutlinedTextWidget {...props} />

      {showAddButton && (
        <React.Fragment>
          <Spacer y={2} />
          <Button
            variant="secondary"
            type="button"
            isLoading={isLoading}
            onClick={handleAddAutomatically}
            prependIcon={<Icons.Gps width={18} />}
            intlId="relining.gpsCoordinates.addAutomatically"
            sx={{
              alignSelf: 'flex-start',
              width: 'fit-content',
              px: 4,
              py: 2,
              fontSize: 'sm',
            }}
          />
        </React.Fragment>
      )}

      {errorKey && (
        <React.Fragment>
          <Spacer y={2} />
          <Text
            as="p"
            intlId={errorKey}
            sx={{ color: 'error', fontSize: 'sm' }}
          />
        </React.Fragment>
      )}
    </Box>
  );
}

